/**
 * Platform root domain — ENV-driven, NEVER hardcoded.
 *
 * The root domain is the global platform boundary (auth, workspace selection,
 * platform admin). Workspaces live on `<slug>.<ROOT_DOMAIN>`. All URLs are
 * derived from `VITE_FRONTEND_URL` / `VITE_ROOT_DOMAIN` so the same build runs
 * in dev, staging, QA, production and white-label deployments.
 */

function cleanOrigin(v: string): string {
  return v.replace(/\/+$/, '');
}

/** The configured platform frontend origin (fallback: current window origin). */
export function getFrontendUrl(): string {
  const env = import.meta.env;
  const fromEnv =
    (env.VITE_FRONTEND_URL as string) ||
    (env.VITE_ROOT_DOMAIN ? `https://${env.VITE_ROOT_DOMAIN}` : '');
  if (fromEnv) return cleanOrigin(fromEnv);
  if (typeof window !== 'undefined') return cleanOrigin(window.location.origin);
  return 'http://localhost:5173';
}

/** Normalize a root-domain value: strip scheme + trailing slashes + leading
 *  'www.', lowercase. */
function cleanRootDomain(v: string): string {
  return v
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
    .replace(/^www\./, '')
    .toLowerCase();
}

/** Derive the workspace ROOT domain from the platform/frontend URL hostname by
 *  stripping the platform subdomain. Platform `app.isats.org` ⇒ root
 *  `isats.org`; platform `isats.org` (no subdomain) stays `isats.org`. Workspace
 *  slugs ALWAYS sit on this root domain — never on the platform subdomain. */
function rootFromFrontendHost(host: string): string {
  const parts = host.split('.');
  if (parts.length >= 3) return parts.slice(1).join('.');
  return host;
}

/** The workspace ROOT domain (hostname without scheme) — the domain workspace
 *  slugs are appended to: workspace `xyz` on root `isats.org` ⇒ `xyz.isats.org`.
 *  Authoritative source: `VITE_ROOT_DOMAIN`. When unset it is derived from the
 *  platform/frontend URL by stripping the platform subdomain (so `app.isats.org`
 *  still yields `isats.org`), never the platform host itself. */
export function getRootDomain(): string {
  const rd = import.meta.env.VITE_ROOT_DOMAIN as string;
  if (rd) return cleanRootDomain(rd);
  try {
    return rootFromFrontendHost(cleanRootDomain(new URL(getFrontendUrl()).hostname));
  } catch {
    return 'localhost';
  }
}

/** Build a workspace subdomain URL: `https://<slug>.<ROOT_DOMAIN>`.
 *
 * MJ-10 FIX: host-shape aware. On hosts where subdomain routing is impossible,
 * return the CURRENT origin so the caller falls back to a same-origin reload
 * (the workspace context then travels via the X-Workspace-Id header):
 * - `*.run.app` (Cloud Run): the first label is a service name, not a
 *   subdomain — `<slug>.europe-west1.run.app` can never resolve. The single
 *   service hosts all workspaces; the header selects the tenant.
 * - localhost / IP literals: never cross-host navigate.
 * Also fixes the dead ternary (was always 'https:').
 */
export function workspaceSubdomainUrl(slug: string): string {
  try {
    const current = new URL(getFrontendUrl());
    const host = current.hostname.toLowerCase();
    const isRunApp = /\.run\.app$/.test(host);
    const isLocal = host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host.startsWith('[');
    if (isRunApp || isLocal) return current.origin;
  } catch { /* fall through to default */ }
  const root = getRootDomain();
  const proto = /^https:/.test(getFrontendUrl()) ? 'https:' : 'http:';
  return `${proto}//${slug}.${root}`;
}

/** Human-readable workspace subdomain host: `<slug>.<ROOT_DOMAIN>` (env-driven). */
export function workspaceSubdomainHost(slug: string): string {
  return `${slug}.${getRootDomain()}`;
}

/**
 * Resolve the workspace slug from the current URL subdomain.
 * Returns null when no workspace subdomain is present (i.e. the request is
 * on the platform root domain).
 */
export function resolveWorkspaceSlugFromUrl(hostname?: string): string | null {
  const host = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  const root = getRootDomain().toLowerCase();
  if (!root || root === 'localhost') return null;
  if (!host.endsWith(`.${root}`)) return null;
  const sub = host.slice(0, host.length - root.length - 1);
  if (!sub) return null;
  return sub;
}
